import { generateShortNameAndBrand } from 'actions'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import internalOnlyApi from 'services/api/internalOnly'

import styles from './Occupancy.module.scss'

function generateHotelGroups(hotelData) {
  return hotelData.reduce((prev, curr) => {
    if (!curr.groupName) {
      return prev
    }

    return {
      ...prev,
      [curr.groupName]:
        curr.groupName === 'Independently Managed Hotels'
          ? '- Independently managed'
          : curr.groupName
    }
  }, {})
}

function generateHotelChains(hotelData) {
  const hotelChains = hotelData.reduce((prev, curr) => {
    if (!curr.chainCode) {
      return prev
    }

    return { ...prev, [curr.chainCode]: curr.chainCode }
  }, {})

  return hotelChains
}

const occupancyColors = {
  '0-20': '#76F5C7',
  '20-40': '#8fe4ad',
  '40-60': '#EEC239',
  '60-80': '#EE793D',
  '80-100': '#C94444'
}

export const HotelNameCell = ({ hotelName }) => {
  const { brand, shortName } = generateShortNameAndBrand(hotelName)

  return (
    <div className={styles.hotelCell}>
      <p className={styles.hotelBrand}>{brand}</p>
      <p className={styles.shortHotelName}>{shortName}</p>
    </div>
  )
}

const LegendBar = ({ color, label }) => (
  <div className={styles.legendBar}>
    <div style={{ backgroundColor: color }} className={styles.legendBarColor}></div>
    <p className={styles.legendLabel}>{label}</p>
  </div>
)

const Legend = () => (
  <div className={styles.legend}>
    <LegendBar color={occupancyColors['0-20']} label="0-20" />
    <LegendBar color={occupancyColors['20-40']} label="20-40" />
    <LegendBar color={occupancyColors['40-60']} label="40-60" />
    <LegendBar color={occupancyColors['60-80']} label="60-80" />
    <LegendBar color={occupancyColors['80-100']} label="80-100" />
  </div>
)

const Tooltip = ({ date, occupancy, emptyRooms }) => (
  <div className={styles.tooltip}>
    <p className={styles.tooltipTitle}>{moment(date).format('MMM DD')}</p>
    <p className={styles.tooltipText}>{occupancy.toFixed(2)}% full</p>
    <p className={styles.tooltipText}>{emptyRooms} rooms available</p>
  </div>
)

export const OccupancyCell = ({ value, date, emptyRooms, height = 35 }) => {
  let range

  if (value < 20) {
    range = '0-20'
  } else if (value < 40) {
    range = '20-40'
  } else if (value < 60) {
    range = '40-60'
  } else if (value < 80) {
    range = '60-80'
  } else {
    range = '80-100'
  }

  return (
    <div className={styles.occupancyCellHolder}>
      <div
        className={styles.occupancyCell}
        style={{
          backgroundColor: range ? occupancyColors[range] : 'gray',
          height
        }}
      ></div>
      <Tooltip date={date} occupancy={value} emptyRooms={emptyRooms} />
    </div>
  )
}

const Occupancy = () => {
  const groupBy = 'city'

  const [allHotelGroups, setAllHotelGroups] = useState()
  const [allHotelChains, setAllHotelChains] = useState()

  const [data, setData] = useState([])

  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)
  const [error, setError] = useState()

  useEffect(() => {
    internalOnlyApi
      .occupancy({
        fromDate: moment().format('YYYY-MM-DD'),
        toDate: moment().add(30, 'days').format('YYYY-MM-DD')
      })
      .then(res => {
        setAllHotelChains(generateHotelChains(res))
        setAllHotelGroups(generateHotelGroups(res))

        setData(res)

        setIsLoading(false)
      })
      .catch(err => {
        setIsErred(true)
        setError(err)
        setIsLoading(false)
      })
  }, [])

  const [selectedHotelGroup, setSelectedHotelGroup] = useState('all')
  const [selectedHotelChain, setSelectedHotelChain] = useState('all')
  const [selectedHotelBrand, setSelectedHotelBrand] = useState('all')

  const [filterQuery, setFilterQuery] = useState('')

  const filteredData = data
    .filter(hotel =>
      selectedHotelGroup === 'all' ? true : hotel.groupName === selectedHotelGroup
    )
    .filter(hotel =>
      selectedHotelChain === 'all' ? true : hotel.chainCode === selectedHotelChain
    )
    .filter(hotel =>
      selectedHotelBrand === 'all' ? true : hotel.parentChainCode === selectedHotelBrand
    )
    .filter(hotel =>
      [hotel.hotelName, hotel.city, hotel.state, hotel.country]
        .join(' ')
        .toUpperCase()
        .includes(filterQuery.toUpperCase())
    )

  const groupedHotels = filteredData.reduce((acc, hotel) => {
    const { city, state, country } = hotel

    if (groupBy === 'state') {
      if (acc[state]) {
        return { ...acc, [state]: [...acc[state], hotel] }
      } else {
        return { ...acc, [state]: [hotel] }
      }
    }

    if (groupBy === 'city') {
      if (acc[city]) {
        return { ...acc, [city]: [...acc[city], hotel] }
      } else {
        return { ...acc, [city]: [hotel] }
      }
    }

    if (groupBy === 'country') {
      if (acc[country]) {
        return { ...acc, [country]: [...acc[country], hotel] }
      } else {
        return { ...acc, [country]: [hotel] }
      }
    }

    return null
  }, {})

  if (isErred) {
    return (
      <div className={styles.wrapper}>
        <CustomHeader title="Occupancy" isFixed={true} width={1180} />
        <p className={styles.noResults}>
          Something went wrong while getting the occupancy data: {error.toString()}
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <CustomHeader title="Occupancy" isFixed={true} width={1180} />
        <LoadingIndicator />
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.filterBar}>
        <Dropdown
          value={selectedHotelGroup}
          onChange={e => setSelectedHotelGroup(e.target.value)}
          label="Hotel group"
          options={{
            all: 'All groups',
            ...allHotelGroups
          }}
        />
        <Dropdown
          value={selectedHotelBrand}
          label="Hotel brand"
          onChange={e => setSelectedHotelBrand(e.target.value)}
          options={{
            all: 'All brands',
            ...parentChainNames
          }}
        />
        <Dropdown
          value={selectedHotelChain}
          label="Hotel chain"
          onChange={e => setSelectedHotelChain(e.target.value)}
          options={{
            all: 'All chains',
            ...allHotelChains
          }}
        />
        <TextInput
          label="Search"
          type="search"
          style={{ width: '250px', backgroundColor: 'white' }}
          placeholder="enter hotel name, city or state"
          value={filterQuery}
          onChangeValue={setFilterQuery}
        />
        <Legend />
      </div>
      <CustomHeader title="Occupancy" isFixed={true} width={1180} />
      {filteredData.length === 0 ? (
        <p className={styles.noResults}>No matching hotels for selected filters</p>
      ) : (
        <>
          <div className={styles.titleRow}>
            <div className={styles.titleRowDays}>
              {filteredData[0].occupancyByDays.map((day, index) => {
                return (
                  <div key={index}>
                    <div className={styles.titleRowMonth}>
                      {filteredData[0].occupancyByDays[index - 1] ? (
                        moment(filteredData[0].occupancyByDays[index - 1].date).isSame(
                          day.date,
                          'month'
                        ) ? (
                          <span className={styles.monthLine}></span>
                        ) : (
                          moment(day.date).format('MMM')
                        )
                      ) : (
                        moment(day.date).format('MMM')
                      )}
                    </div>
                    <div
                      className={
                        [0, 6].includes(moment(day.date).day())
                          ? styles.occupancyDateWeekend
                          : styles.occupancyDate
                      }
                    >
                      {moment(day.date).format('D')}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          <div className={styles.rowGroups}>
            {Object.keys(groupedHotels)
              .sort((a, b) =>
                groupedHotels[a][0].state.localeCompare(groupedHotels[b][0].state)
              )
              .map(groupCategory => (
                <div key={groupCategory}>
                  <p className={styles.groupTitle}>
                    {groupCategory}, {groupedHotels[groupCategory][0].state}
                  </p>
                  <div className={styles.rowGroup}>
                    {groupedHotels[groupCategory].map(hotel => {
                      return (
                        <div className={styles.row}>
                          <HotelNameCell hotelName={hotel.hotelName} />
                          {hotel.occupancyByDays.map(day => {
                            return (
                              <OccupancyCell
                                value={day.occupancy}
                                date={day.date}
                                emptyRooms={day.emptyRooms}
                                key={day.date}
                              />
                            )
                          })}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  )
}

export default Occupancy
